import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Phone,
  PhoneForwarded,
  Settings,
  Plus,
  Trash2,
  FileText,
  FileCheck,
  Pill,
  AlertCircle,
  AlertTriangle,
  HelpCircle,
  FileQuestion,
  MoreHorizontal,
  Clock,
  Save,
  Loader2,
  Mail,
  MessageSquare,
  PhoneIncoming
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mapping des icônes par catégorie
const CATEGORY_ICONS = {
  'results': FileText,
  'certificate': FileCheck,
  'prescription_renewal': Pill,
  'urgent_question': AlertCircle,
  'side_effects': AlertTriangle,
  'medical_question': HelpCircle,
  'administrative': FileQuestion,
  'other': MoreHorizontal
};

// Labels des actions en français
const ACTION_LABELS = {
  'transfer': 'Transférer au médecin',
  'voicemail': 'Laisser un message vocal',
  'callback': 'Demander rappel',
  'sms_notify': 'Notifier par SMS'
};

const ACTION_ICONS = {
  'transfer': PhoneForwarded,
  'voicemail': Mail,
  'callback': PhoneIncoming,
  'sms_notify': MessageSquare
};

const DAYS_OF_WEEK = [
  { value: 1, label: 'Lundi', short: 'Lun' },
  { value: 2, label: 'Mardi', short: 'Mar' },
  { value: 3, label: 'Mercredi', short: 'Mer' },
  { value: 4, label: 'Jeudi', short: 'Jeu' },
  { value: 5, label: 'Vendredi', short: 'Ven' },
  { value: 6, label: 'Samedi', short: 'Sam' },
  { value: 7, label: 'Dimanche', short: 'Dim' }
];

export const CallTransferConfig = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customCategoryName, setCustomCategoryName] = useState('');
  const [customCategoryDesc, setCustomCategoryDesc] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  // Charger la configuration
  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/call-transfer-config/config`, { headers });
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Erreur lors du chargement de la configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  // Sauvegarder la configuration globale
  const saveConfig = async (updates) => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/call-transfer-config/config`,
        updates,
        { headers }
      );
      setConfig(response.data);
      toast.success('Configuration sauvegardée');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Mettre à jour une catégorie
  const updateCategory = async (categoryId, enabled, action) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/call-transfer-config/category/${categoryId}`,
        { category_id: categoryId, enabled, action },
        { headers }
      );
      setConfig(response.data.config);
      toast.success('Catégorie mise à jour');
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Ajouter une catégorie personnalisée
  const addCustomCategory = async () => {
    if (!customCategoryName.trim()) {
      toast.error('Veuillez entrer un nom pour la catégorie');
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/call-transfer-config/custom-category`,
        {
          name: customCategoryName,
          description: customCategoryDesc,
          action: 'transfer'
        },
        { headers }
      );
      setConfig(response.data.config);
      setCustomCategoryName('');
      setCustomCategoryDesc('');
      setShowAddCustom(false);
      toast.success('Catégorie personnalisée ajoutée');
    } catch (error) {
      console.error('Error adding custom category:', error);
      toast.error('Erreur lors de l\'ajout');
    }
  };

  // Supprimer une catégorie personnalisée
  const removeCustomCategory = async (categoryId) => {
    try {
      const response = await axios.delete(
        `${API_URL}/api/call-transfer-config/custom-category/${categoryId}`,
        { headers }
      );
      setConfig(response.data.config);
      toast.success('Catégorie supprimée');
    } catch (error) {
      console.error('Error removing category:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Toggle jour de la semaine
  const toggleDay = (dayValue) => {
    const currentDays = config?.availability_hours?.days || [];
    let newDays;
    if (currentDays.includes(dayValue)) {
      newDays = currentDays.filter(d => d !== dayValue);
    } else {
      newDays = [...currentDays, dayValue].sort();
    }
    
    saveConfig({
      availability_hours: {
        ...config.availability_hours,
        days: newDays
      }
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2">Chargement...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="call-transfer-config">
      {/* En-tête avec activation globale */}
      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <PhoneForwarded className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <CardTitle>Configuration des Transferts d'Appel</CardTitle>
                <CardDescription>
                  Définissez quand l'assistant vocal doit transférer les appels vers vous
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="transfer-enabled" className="text-sm font-medium">
                {config?.enabled ? 'Activé' : 'Désactivé'}
              </Label>
              <Switch
                id="transfer-enabled"
                checked={config?.enabled || false}
                onCheckedChange={(checked) => saveConfig({ enabled: checked })}
                data-testid="transfer-enabled-switch"
              />
            </div>
          </div>
        </CardHeader>
      </Card>

      {config?.enabled && (
        <>
          {/* Numéro de transfert */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Phone className="w-5 h-5" />
                Numéro de Transfert
              </CardTitle>
              <CardDescription>
                Numéro vers lequel les appels seront transférés (laissez vide pour utiliser le numéro de votre profil)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Input
                  type="tel"
                  placeholder="+33 6 12 34 56 78"
                  value={config?.transfer_phone || ''}
                  onChange={(e) => setConfig({ ...config, transfer_phone: e.target.value })}
                  className="max-w-xs"
                  data-testid="transfer-phone-input"
                />
                <Button
                  variant="outline"
                  onClick={() => saveConfig({ transfer_phone: config?.transfer_phone })}
                  disabled={saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  <span className="ml-2">Enregistrer</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Horaires de disponibilité */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Horaires de Disponibilité
                  </CardTitle>
                  <CardDescription>
                    Définissez quand vous êtes disponible pour recevoir des transferts
                  </CardDescription>
                </div>
                <Switch
                  checked={config?.availability_hours?.enabled || false}
                  onCheckedChange={(checked) => saveConfig({
                    availability_hours: { ...config.availability_hours, enabled: checked }
                  })}
                  data-testid="availability-hours-switch"
                />
              </div>
            </CardHeader>
            {config?.availability_hours?.enabled && (
              <CardContent className="space-y-4">
                {/* Jours de la semaine */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Jours de disponibilité</Label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map((day) => {
                      const isSelected = config?.availability_hours?.days?.includes(day.value);
                      return (
                        <Button
                          key={day.value}
                          variant={isSelected ? "default" : "outline"}
                          size="sm"
                          onClick={() => toggleDay(day.value)}
                          className={isSelected ? "bg-blue-600 hover:bg-blue-700" : ""}
                          data-testid={`day-${day.value}`}
                        >
                          {day.short}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {/* Heures */}
                <div className="flex gap-4 items-center">
                  <div>
                    <Label className="text-sm">De</Label>
                    <Input
                      type="time"
                      value={config?.availability_hours?.start || '08:00'}
                      onChange={(e) => saveConfig({
                        availability_hours: { ...config.availability_hours, start: e.target.value }
                      })}
                      className="w-32"
                      data-testid="availability-start-time"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">À</Label>
                    <Input
                      type="time"
                      value={config?.availability_hours?.end || '19:00'}
                      onChange={(e) => saveConfig({
                        availability_hours: { ...config.availability_hours, end: e.target.value }
                      })}
                      className="w-32"
                      data-testid="availability-end-time"
                    />
                  </div>
                </div>

                {/* Action hors horaires */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    Action si vous n'êtes pas disponible
                  </Label>
                  <Select
                    value={config?.fallback_action || 'voicemail'}
                    onValueChange={(value) => saveConfig({ fallback_action: value })}
                  >
                    <SelectTrigger className="w-64" data-testid="fallback-action-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="voicemail">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Laisser un message vocal
                        </div>
                      </SelectItem>
                      <SelectItem value="callback">
                        <div className="flex items-center gap-2">
                          <PhoneIncoming className="w-4 h-4" />
                          Proposer un rappel
                        </div>
                      </SelectItem>
                      <SelectItem value="sms_notify">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          Vous notifier par SMS
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Catégories de transfert */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Motifs de Transfert
                  </CardTitle>
                  <CardDescription>
                    Cochez les situations où l'assistant doit vous transférer l'appel
                  </CardDescription>
                </div>
                <Dialog open={showAddCustom} onOpenChange={setShowAddCustom}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="add-custom-category-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter une catégorie
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Nouvelle catégorie personnalisée</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Nom de la catégorie</Label>
                        <Input
                          value={customCategoryName}
                          onChange={(e) => setCustomCategoryName(e.target.value)}
                          placeholder="Ex: Suivi post-opératoire"
                          data-testid="custom-category-name-input"
                        />
                      </div>
                      <div>
                        <Label>Description (optionnel)</Label>
                        <Input
                          value={customCategoryDesc}
                          onChange={(e) => setCustomCategoryDesc(e.target.value)}
                          placeholder="Ex: Patient ayant subi une opération récente"
                          data-testid="custom-category-desc-input"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAddCustom(false)}>
                        Annuler
                      </Button>
                      <Button onClick={addCustomCategory} data-testid="save-custom-category-btn">
                        Ajouter
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Catégories prédéfinies */}
                {config?.categories?.map((category) => {
                  const IconComponent = CATEGORY_ICONS[category.category_id] || HelpCircle;
                  const ActionIcon = ACTION_ICONS[category.action] || PhoneForwarded;
                  
                  return (
                    <div
                      key={category.category_id}
                      className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                        category.enabled 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-gray-50 border-gray-200'
                      }`}
                      data-testid={`category-${category.category_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          category.enabled ? 'bg-green-100' : 'bg-gray-200'
                        }`}>
                          <IconComponent className={`w-5 h-5 ${
                            category.enabled ? 'text-green-600' : 'text-gray-500'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium">{category.name_fr}</p>
                          <p className="text-sm text-gray-500">{category.description_fr}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {category.enabled && (
                          <Select
                            value={category.action || 'transfer'}
                            onValueChange={(value) => updateCategory(category.category_id, true, value)}
                          >
                            <SelectTrigger className="w-48">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(ACTION_LABELS).map(([value, label]) => {
                                const ActionOptionIcon = ACTION_ICONS[value];
                                return (
                                  <SelectItem key={value} value={value}>
                                    <div className="flex items-center gap-2">
                                      <ActionOptionIcon className="w-4 h-4" />
                                      {label}
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        )}
                        
                        <Switch
                          checked={category.enabled}
                          onCheckedChange={(checked) => updateCategory(
                            category.category_id, 
                            checked, 
                            category.action || 'transfer'
                          )}
                          data-testid={`category-switch-${category.category_id}`}
                        />
                      </div>
                    </div>
                  );
                })}

                {/* Catégories personnalisées */}
                {config?.custom_categories?.map((category) => (
                  <div
                    key={category.id}
                    className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                      category.enabled 
                        ? 'bg-purple-50 border-purple-200' 
                        : 'bg-gray-50 border-gray-200'
                    }`}
                    data-testid={`custom-category-${category.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        category.enabled ? 'bg-purple-100' : 'bg-gray-200'
                      }`}>
                        <MoreHorizontal className={`w-5 h-5 ${
                          category.enabled ? 'text-purple-600' : 'text-gray-500'
                        }`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{category.name}</p>
                          <Badge variant="outline" className="text-xs">Personnalisé</Badge>
                        </div>
                        {category.description && (
                          <p className="text-sm text-gray-500">{category.description}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => removeCustomCategory(category.id)}
                        data-testid={`remove-custom-${category.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      
                      <Switch
                        checked={category.enabled}
                        onCheckedChange={(checked) => {
                          // Pour les catégories personnalisées, on met à jour localement
                          const updatedCustom = config.custom_categories.map(c =>
                            c.id === category.id ? { ...c, enabled: checked } : c
                          );
                          saveConfig({ custom_categories: updatedCustom });
                        }}
                        data-testid={`custom-switch-${category.id}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Options de notification */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">SMS lors des transferts</p>
                  <p className="text-sm text-gray-500">
                    Recevoir un SMS quand un appel est transféré vers vous
                  </p>
                </div>
                <Switch
                  checked={config?.sms_notify_on_transfer || false}
                  onCheckedChange={(checked) => saveConfig({ sms_notify_on_transfer: checked })}
                  data-testid="sms-notify-switch"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Email pour les messages vocaux</p>
                  <p className="text-sm text-gray-500">
                    Recevoir un email quand un patient laisse un message vocal
                  </p>
                </div>
                <Switch
                  checked={config?.voicemail_email_notify || false}
                  onCheckedChange={(checked) => saveConfig({ voicemail_email_notify: checked })}
                  data-testid="voicemail-email-switch"
                />
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default CallTransferConfig;
